/**
 * Work Order status machine — pure, node-testable. Mirrors the firestore.rules
 * `woStatusTransitionOk()` helper exactly (incl. the same-status branch, so
 * notes / photo edits on a WO are allowed).
 *
 * We operate a reduced subset of the WorkOrderStatus union: DRAFT → IN_PROGRESS
 * → COMPLETED, plus → CANCELLED from any non-terminal state. SCHEDULED /
 * QC_PASSED / READY_FOR_DELIVERY stay dormant in the type for a future
 * scheduling/QC layer.
 */
import type { WorkOrderStatus } from '../../../types';

export const OPERATED_WORK_ORDER_STATUSES: readonly WorkOrderStatus[] = [
  'DRAFT',
  'IN_PROGRESS',
  'COMPLETED',
  'CANCELLED',
];

const TERMINAL: readonly WorkOrderStatus[] = ['COMPLETED', 'CANCELLED'];

const FORWARD: Partial<Record<WorkOrderStatus, WorkOrderStatus[]>> = {
  DRAFT: ['IN_PROGRESS'],
  IN_PROGRESS: ['COMPLETED'],
};

/** Whether a WO may move from `from` to `to`. Same-status is allowed
 *  (metadata-only writes) except for CANCELLED; CANCELLED is reachable
 *  from any non-terminal state. */
export function canAdvanceWorkOrder(from: WorkOrderStatus, to: WorkOrderStatus): boolean {
  if (from === to && to !== 'CANCELLED') return true;
  if (to === 'CANCELLED') return !TERMINAL.includes(from);
  return (FORWARD[from] ?? []).includes(to);
}

export const isOpenWorkOrder = (status: WorkOrderStatus): boolean => !TERMINAL.includes(status);
